<template>
    <div>
      <!-- Inline -->
      <div class="row q-mb-md">
        <!-- Filters hide/show button -->
        <div class="col-6">
          <div class="row" style="justify-content: flex-start">
            <div>
              <q-btn
                @click="showFilters();"
                :text-color="hideFilters ? 'grey-5' : 'white'"
                class="neutral-btn ctrl-button btn-compact"
                :class="{ primary: !hideFilters }"
                icon="filter_list"
                :icon-right="hideFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
              />
            </div>
            <!-- General searchfield -->
            <div class="q-ml-md" style="flex-grow: 1">
              <q-input
                :disable="!hideFilters || activeFilters"
                color="grey-5"
                v-model="searchTerm"
                @input="applyFilters"
                :placeholder="getString(null, 'Search directly by name or url', 'Zoek direct op naam of Url')"
              />
            </div>
          </div>
        </div>
        <!-- Add button -->
        <div class="col-6" style="justify-content: flex-end">
          <q-btn v-if="$store.state.meStore.me.rights.includes('magazine.add') || $store.state.meStore.me.su"
            class="capitalize ctrl-button right"
            color="ctrl-turquoise"
            :label="getString('action.add')"
            @click="$router.push(`magazine/add`)"
            icon-right="add"
          />
          <q-btn v-if="$store.state.meStore.me.rights.includes('page.delete') || $store.state.meStore.me.su"
            class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
            color="ctrl-turquoise"
            :disabled="!rowSelection.length"
            :label="$store.state.stringsStore.strings.list.actions.label"
            :class="{ 'button-active': rowSelection.length }">
              <q-icon size="large" name="mdi-chevron-down"/>
              <i v-show="rowSelection.length" class="counter">{{rowSelection.length}}</i>
              <!-- Direct child of target -->
              <q-popover anchor="bottom middle" self="top middle">
                <q-list link>
                  <q-item
                    :class="{ 'disabled': !rowSelection.length }"
                    class="toolbar-list-item" @click.native="removeRows('magazines')">
                    <q-icon color="positive" size="large" name="delete"/>
                    {{$store.state.stringsStore.strings.action.remove}}
                  </q-item>
                </q-list>
              </q-popover>
          </q-btn>
        </div>
      </div>
      <!-- Filters block -->
      <transition name="fade">
      <div v-if="!hideFilters" class="table-block q-mb-md">
        <div class="row q-pa-md" style="justify-content: space-between;">
          <!-- Searchfield -->
          <div class="col-6 q-pa-md">
            <q-input color="primary"
              v-model="filters.name"
              @input="applyFilters"
              :float-label="getString('name')"/>
          </div>
          <!-- Searchfield -->
          <div class="col-6 q-pa-md">
            <q-input color="primary"
            v-model="filters.url"
            @input="applyFilters"
            float-label="Url"/>
          </div>
        </div>
        <div class="row q-pa-md" style="justify-content: space-between;">
          <div class="q-pa-md" style="vertical-align: middle;">
            <q-btn
              :disable="!activeFilters"
              @click="resetFilters()"
              class="capitalize ctrl-button"
              :color="activeFilters ? 'primary' : 'grey-4'"
              :label="getString('action.reset')"
            />
          </div>
        </div>
      </div>
      </transition>
      <!-- Data table -->
      <div class="q-table" id="myTable">
        <vuetable
          ref="vuetable"
          class="table-block q-mb-md"
          :class="{ 'is-loading': isLoading }"
          :table-height="tableHeight"
          api-url="magazine"
          pagination-path
          :api-mode="true"
          :css="css.table"
          :noDataTemplate="noDataString('magazine')"
          :http-fetch="fetchData"
          :fields="columns"
          :row-class="selectable ? applyRowClass : null"
          @vuetable:loading="onLoading"
          @vuetable:load-success="onSuccess"
          @vuetable:load-error="onError"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:row-clicked="onRowClicked">
          <div slot="name" slot-scope="props">
            <span v-html="highlight(props.rowData.name, filters.name)"></span>
          </div>
          <div slot="url" slot-scope="props">
            <span v-html="highlight(props.rowData.url, filters.url)"></span>
          </div>
          <div slot="active" slot-scope="props">
            <q-toggle color="secondary" type="text" v-model="props.rowData.active"/>
          </div>
        </vuetable>
        <!-- Pagination -->
        <div class="row pagination-bottom" id="myPagination">
          <!-- Per page select -->
          <div class="col">
            <div class="row">
              <span class="q-mr-sm grey">
                {{ $store.state.stringsStore.strings.list.itemsPerPage }}
              </span>
              <q-select
                class="btn-compact select-small"
                hide-underline
                color="grey"
                v-model="perPage"
                :options="perPageOptions"
              />
            </div>
          </div>
          <div class="col right-margin">
            <vuetable-pagination
              :on-each-side="1"
              :css="css.pagination"
              @vuetable-pagination:change-page="onChangePage"
              ref="pagination">
            </vuetable-pagination>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';

import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import selectRows from '@/mixins/tables/selectRows';

export default {
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    selectRows,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  data() {
    return {
      isLoading: false,
      searchTerm: '',
      responseData: {
        multiple: 'magazines',
      },
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'magazine',
      // Where data is fetched from
      endpoint: 'magazine',
      // Used for navigation on the front-end (matching the router)
      module: 'magazine',
      filters: {
        name: '',
        address: '',
        city: '',
      },
      columns: [
        {
          name: '__slot:name',
          title: getString('name'),
          sortField: 'name',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
        },
        {
          name: '__slot:url',
          title: 'Url',
          titleClass: 'text-left vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
        },
        {
          name: '__slot:active',
          title: getString(null, 'Active', 'Actief'),
          sortField: 'city',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
        },
      ],
      hideFilters: true,
      preferences: {
        page: 1,
        perPage: 10,
        sortField: null,
        sortOrder: null,
      },
    };
  },
  methods: {
    getString,
  },
};
</script>
